import { DecimalPipe } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnChanges,
  computed,
  inject,
  input,
} from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';

import moment from 'moment';

import { TimeTable } from '../service/jira';

interface Row {
  name: string;
  label: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <table mat-table [dataSource]="rows()" [trackBy]="trackBy">
      <ng-container sticky matColumnDef="labels">
        <th mat-header-cell *matHeaderCellDef>Periods</th>
        <td mat-cell *matCellDef="let row">{{ row.label }}</td>
      </ng-container>

      @for (rec of table().time; track rec[0]) {
        <ng-container [matColumnDef]="rec[0]">
          <th mat-header-cell *matHeaderCellDef>
            {{ rec[1].long_label || rec[0] }}
          </th>

          <td mat-cell *matCellDef="let row">
            @let value = rec[1][row.name];
            @let initial = rec[1][row.name + '_initial'];
@if(['overused', 'spent'].includes(row.name) && value){-}{{ value | number: '.0-2' }}

            @if (initial) {
              <mat-chip>
                <span matTooltip="Tickets" class="inline-flex">
                  {{ value - initial | number: '.0-2' }}
                  <mat-icon inline>task</mat-icon>
                </span>
                +
                <span matTooltip="Hours" class="inline-flex">
                  {{ initial | number: '.0-2' }}
                  <mat-icon inline>schedule</mat-icon>
                </span>
              </mat-chip>
            }
          </td>
        </ng-container>
      }

      <tr mat-header-row *matHeaderRowDef="['labels'].concat(dates())"></tr>
      <tr mat-row *matRowDef="let row; columns: ['labels'].concat(dates())"></tr>
    </table>
  `,
  styles: [
    `
      :host {
        overflow-x: auto;
        display: block;
      }
    `,
    'table {width: max-content;}',
    `
      th:nth-child(6n-3),
      th:nth-child(6n-4),
      th:nth-child(6n-2) {
        background: var(--mat-sys-primary);
        color: var(--mat-sys-on-primary);
      }
    `,
  ],
  selector: 'lcm-time-reporting-table',
  imports: [MatTableModule, MatTooltipModule, MatChipsModule, DecimalPipe, MatIconModule],
})
export class TimeReportingTable implements AfterViewChecked, OnChanges {
  private shouldScroll = false;

  private elementRef = inject(ElementRef);

  table = input.required<TimeTable>();

  dates = computed(() => this.table().time.map(([date]) => date));

  rows = computed<Row[]>(() => {

    const data = this.table();
    const lastUpdated = data.lastUpdated ? moment(data.lastUpdated).utc().local().calendar() : 'Loading...';
    return [
      { name: 'spent', label: `Hours spent (${lastUpdated})` },
      { name: 'unused', label: 'Unused hours carried over' },
      { name: 'agreement_support', label: 'Agreement support hours' },
      { name: 'overused', label: 'Overused hours carried over' },
      { name: 'purchased', label: 'Support hours purchased' },
      { name: 'uncharged', label: 'Unchargeable time' },
      { name: 'unforeseen', label: 'Unforeseen time' },
      { name: 'profited', label: 'Profitable time' },
      { name: 'remaining', label: 'Hours remaining' },
    ];
  });

  trackBy(idx: number, row: Row) {
    return row.name;
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollTable();
      this.shouldScroll = false;
    }
  }

  ngOnChanges() {
    this.shouldScroll = true;
  }

  scrollTable() {
    this.elementRef.nativeElement.scrollLeft = this.elementRef.nativeElement.scrollWidth;
  }
}
